import type { ArtifactFormat, GeneratedIssueContent, IssueTemplate } from '@tsuzuri/contracts';

import { assertSupportedFormat, renderProfiles, templateManifests } from './manifest';
import { renderIssueDocument } from './render';
import type { RenderImage, RenderIssueInput } from './render';

export type LayoutMockId = `${IssueTemplate}--${ArtifactFormat}`;

export interface LayoutMockCatalogItem {
  readonly id: LayoutMockId;
  readonly template: IssueTemplate;
  readonly templateVersion: string;
  readonly templateName: string;
  readonly templateDescription: string;
  readonly format: ArtifactFormat;
  readonly formatName: string;
  readonly profile: Readonly<{
    mimeType: 'application/pdf' | 'image/jpeg';
    width: number;
    height: number;
    unit: 'mm' | 'px';
    dpi: number;
    safeMargin: number;
  }>;
}

interface LayoutMockFixture {
  issueLabel: string;
  publishedOn: string;
  content: GeneratedIssueContent;
}

const formatNames: Record<ArtifactFormat, string> = {
  preview_jpeg: '標準プレビュー',
  sns_4x5_jpeg: 'SNS 4:5',
  story_9x16_jpeg: 'ストーリーズ 9:16',
  a4_pdf: 'A4 PDF',
  a3_pdf: 'A3 PDF',
  two_l_jpeg: '2L 写真',
  postcard_jpeg: 'はがき',
};

const article = (id: number, headline: string, body: string, caption: string | null = null) => ({
  headline,
  body,
  caption,
  sourceEventIds: [`00000000-0000-4000-8000-${String(id).padStart(12, '0')}`],
});

const mockFixtures: Record<IssueTemplate, LayoutMockFixture> = {
  'classic-newspaper': {
    issueLabel: '2026年8月号',
    publishedOn: '2026年8月5日',
    content: {
      title: 'ひとり立ち三秒　家族に歓声',
      lead: '小さな手がテーブルを離れた瞬間、いつもの居間が大きな拍手に包まれました。家族が待ち望んだ、今月いちばんのニュースです。',
      articles: [
        article(
          1,
          '朝の居間で三秒間',
          '一日午前八時すぎ、朝食を終えた居間で、テーブルにつかまっていた小さな手がゆっくりと離れた。家族が息をのんで見守るなか、まっすぐ立っていた時間はおよそ三秒。驚いたように目を丸くしたあと、本人もにっこり笑った。大きな拍手に誘われるように、その後も何度か挑戦。座り込んでは立ち上がり、できた喜びを全身で表していた。昨日までとは少し違う景色が、いつもの居間に広がった朝だった。',
          '8月1日、いつものリビングで',
        ),
        article(
          2,
          '水やり係、始めました',
          'ベランダのミニトマトに水をあげるのが、今月から朝の日課になった。小さなじょうろを両手で持ち、鉢の前まで一歩ずつ運ぶ。最初は葉までびしょぬれにしていたが、今では土の場所を確かめて、少しずつ傾けられるようになった。赤くなった実を見つけると、指をさして家族を呼ぶ。水やりのあとは空のじょうろを元の棚へ戻し、こぼれた水を布で拭くところまで覚えた。晴れた日と雨の日では土の色が違うことにも気づき、じょうろを持つ前に鉢をのぞき込む。家族が「今日はどうかな」と尋ねると、土を指で触って考えるような表情を見せる。今月最初に赤くなった一個は、夕食のサラダに添えた。自分で育てた実を小さく切って口に運ぶと、何度もうなずいていた。葉の枚数や実の色を一緒に確かめた記録は、夏休みの観察日記のように増えている。頼まれなくても朝の支度を始める姿に、家族は新しい成長を感じている。',
        ),
        article(
          3,
          '今月のお気に入り',
          '赤い帽子と恐竜の図鑑がお気に入り。外出の気配を感じると、帽子を自分で選んで玄関まで運ぶ。図鑑では大きな角を持つ恐竜のページを何度も開き、名前を呼んでもらうのを楽しみにしている。',
        ),
        article(
          4,
          '家族で夏祭りへ',
          '近所の夏祭りへ家族で出かけた。色とりどりの金魚をじっと眺め、太鼓が鳴ると周りの人に合わせて手拍子。暗くなると灯った提灯を一つずつ指さし、帰り道まで「あかり」の話が続いた。',
        ),
        article(
          5,
          'ことばの記録',
          '「もう一回」と「自分で」が今月の口ぐせになった。遊びを続けたいときだけでなく、着替えや食事でも自分の気持ちを言葉で伝える。うまくできずに悔しがる場面もあるが、家族が少し待つと、やり直す方法を考えるようになった。食卓では家族の名前を順番に呼び、見つめた相手が返事をすると満足そうにうなずく。「おいしい」「あつい」など、感じたことを表す言葉も増えた。図鑑を開いて知っているものを探したり、散歩で見つけた犬を家に帰ってから話したりする。短い言葉の向こうにある思いが、日に日に豊かになっている。聞き逃してしまいそうな一言も、その日の表情と一緒に書き留めておきたい。',
        ),
        article(
          6,
          '台所からこんにちは',
          '夕食の支度では、とうもろこしの皮むきを手伝った。葉を一枚ずつ引っぱり、ひげが残っていないか確かめる。むき終えた一本を両手で台所まで運ぶと、得意げな表情を見せた。翌日は枝豆をざるへ移す係に挑戦。床へ落ちた一粒も自分で拾い、最後までやり遂げた。料理が始まる音を聞くと、踏み台のそばへ来て「おてつだい」と声をかける。家族は安全にできる仕事を一つずつ相談し、できたことに必ずお礼を伝えている。食卓に並んだとうもろこしを見つけると、自分が準備したことを家族全員に知らせた。いつもの台所が、ものの名前や手順を覚える小さな教室になっている。',
        ),
        article(
          7,
          '雨の日の大冒険',
          '新しい長靴で水たまりへ。最初の一歩では跳ねたしずくに驚いたが、すぐにもう一度挑戦した。浅い場所では小さく足踏みし、深い場所の前では立ち止まって水面をのぞく。傘に落ちる雨音にも耳を澄ませ、強くなると家族の顔を見上げた。公園の木の葉から大粒のしずくが落ちるたび、指をさして行方を追った。帰るころには服の裾までぬらし、声を上げて笑っていた。ぬれた長靴は玄関で自分からそろえ、タオルを持ってくるところまでが今日の大冒険。雨の日にも新しい発見があることを、家族みんなに教えてくれた。',
        ),
        article(
          8,
          '編集部から',
          '何気ない一日のなかにも、あとから読み返したいニュースがいくつもあった。立ち上がった三秒間だけでなく、その前に何度も座り直したことや、見守る家族が息を止めていたことも大切な記録だ。写真に写らない声や、その場で交わした言葉を書き添えると、一枚の出来事が家族みんなの記憶になる。忙しい日は短い一文だけでも残し、月の終わりに順番を並べ直してみたい。小さな変化を見逃さず、家族の言葉で伝えるのがこの新聞の役目です。次号は九月上旬に発行する予定。夏の終わりの遊びや、新しく覚えたことばを中心にお届けします。',
        ),
      ],
      editorsNote:
        '今月の写真係はお父さん、文章係はお母さん。大きな出来事も、いつもの朝の表情も、同じ一か月の大切な記録です。',
    },
  },
  'horizontal-newspaper': {
    issueLabel: '第8号',
    publishedOn: '2026年8月5日',
    content: {
      title: 'はじめての一歩、笑顔ひろがる',
      lead: '小さな成長を、家族の言葉と写真であたたかく見つめます。',
      articles: [
        article(
          41,
          '一歩ずつ進む姿に家族から拍手',
          '今月は、つかまり立ちから手を離して歩く時間が少しずつ増えました。最初は一歩ごとに立ち止まっていましたが、家族が名前を呼ぶと、うれしそうに腕を伸ばして前へ進みます。転んだあとも自分で座り直し、もう一度立ち上がる姿が見られました。朝の居間ではソファからテーブルまで、夕方には窓辺から家族のところまで挑戦しています。歩けた距離よりも、何度でもやってみようとする表情に大きな成長を感じた一か月でした。',
          'お気に入りのおもちゃと過ごした、今月の一枚。',
        ),
        article(
          42,
          'つかまり立ちが安定してきた',
          '行動範囲が広がり、毎日が小さな冒険になっています。',
        ),
        article(
          43,
          '食べる楽しみが増えました',
          '新しい食材にも少しずつ挑戦し、自分から手を伸ばすようになりました。',
        ),
        article(
          44,
          '公園で見つけたお気に入り',
          '木の葉や小石を眺めながら、いつもの散歩をゆっくり楽しみました。',
        ),
        article(
          45,
          '小さな「できた！」が増えるころ',
          '絵本を自分でめくることや、名前を呼ばれて振り向くことなど、毎日の変化をまとめます。',
        ),
        article(
          46,
          '生活リズムをゆっくり整える',
          '眠り、食事、遊びの時間を見直しながら、その日の様子に合わせて過ごしています。',
        ),
        article(
          47,
          '絵本の時間をもっと楽しく',
          '親子でページをめくり、声や表情を交わしながらお気に入りの一冊を楽しみました。',
        ),
        article(
          48,
          '見守りノート',
          '眠りのリズムが整い、朝の支度にも余裕が生まれました。夜は絵本を読んでから静かに休む日が増えています。',
        ),
        article(
          49,
          '次号のお知らせ',
          '来月も、家族が見つけた小さな変化と季節の思い出をお届けします。',
        ),
      ],
      editorsNote:
        '毎日の笑顔にたくさん元気をもらっています。これからも一緒に、いろいろな経験を重ねていこうね。',
    },
  },
  'magazine-cover': {
    issueLabel: '2026年 夏号',
    publishedOn: '2026年8月5日',
    content: {
      title: 'はじめての夏、まぶしい毎日',
      lead: '今しかない表情を一冊の表紙に。',
      articles: [
        article(
          11,
          '小さな冒険家',
          'お気に入りの帽子をかぶって、海辺をどこまでも歩きました。波に驚いて立ち止まった顔も、そのまま表紙に残しています。',
          'お気に入りのおもちゃを手に、今月いちばんの笑顔。',
        ),
        article(
          12,
          '「じぶんで！」が増えました',
          '靴を選ぶ、スプーンを持つ、帽子をかぶる。うまくいかなくても、もう一回。小さな挑戦のたびに見せる真剣な表情と、できたあとの得意な笑顔を集めました。',
        ),
        article(
          13,
          'すいかと、麦わら帽子',
          '赤いすいかをひと口。風鈴の音に耳をすませ、玄関ではお気に入りの帽子を指さします。何度でも出会いたい、この夏の「好き」を小さな図鑑に。',
        ),
        article(
          14,
          'いつもの週末が、宝もの',
          '散歩の帰りに寄った公園。予定のない午後ほど、覚えていたい場面がたくさんありました。',
        ),
        article(
          15,
          'ことばの宝箱',
          '「もういっかい」「どうぞ」「おかえり」。覚えたての声と、そのときの表情を一緒に。',
        ),
        article(
          16,
          '寝顔のとなりで',
          'たくさん遊んだ日の、静かな寝息。今日の発見を家族で振り返ります。',
        ),
        article(17, '小さなおてつだい', '洗濯物を一枚ずつ手渡す、わが家の頼もしい新メンバー。'),
        article(18, '来月のたのしみ', '涼しい朝のおさんぽと、読みかけの絵本のつづきを。'),
      ],
      editorsNote:
        'この夏も小さな冒険をたくさん見せてくれてありがとう。波を見つめた真剣な横顔も、帰り道の笑顔も、ひとつひとつが家族の大切な宝物です。これからの毎日も一緒に楽しもうね。',
    },
  },
  'magazine-cover-portrait': {
    issueLabel: '2026年 夏号',
    publishedOn: '2026年8月5日',
    content: {
      title: '子どもの幸福論！',
      lead: '今この時期の「かわいい」「できた！」を残そう',
      articles: [
        article(
          51,
          '小さな「できた！」が増えるころ',
          'つかまり立ちも、ひと口のごはんも。昨日より少しだけ大きくなったきみ。',
        ),
        article(
          52,
          'お気に入り おもちゃと絵本',
          'ページを開く手が止まるのは、いつも同じところ。今月の「好き」を集めて。',
        ),
        article(
          53,
          '家族で残す ベストショット',
          '笑顔だけでなく、真剣な横顔や眠そうなまなざしも、わが家の大切な一枚。',
        ),
        article(
          54,
          '成長記録 BOOK',
          '一か月の「はじめて」と、家族が見つけた変化を一つにまとめました。',
        ),
        article(
          55,
          'なんでもない日の、しあわせ',
          '並んで歩く帰り道。特別なことがなくても、きみといるだけでうれしい。',
        ),
        article(
          56,
          'きょうは何を見つけた？',
          '木の葉のかたち、雲のゆくえ。小さな指が教えてくれた発見を記録しました。',
        ),
      ],
      editorsNote: '毎月1冊、成長をやさしく記録',
    },
  },
  'magazine-cover-mode': {
    issueLabel: '2026年 秋号',
    publishedOn: '2026年9月7日',
    content: {
      title: 'いつもの\nきみが、\nこんなに\n美しい。',
      lead: '飾らない、そのままの表情を。',
      articles: [
        article(
          80,
          '光のなかで',
          '窓辺の光に目を細めた午後。いつもの表情が、一枚の作品になりました。',
        ),
        article(
          81,
          'わが家の定番',
          '読みかけの絵本と、お気に入りの服。毎日の「好き」を集めました。',
        ),
        article(
          82,
          '小さなまなざし',
          '初めて見つけたものを、じっと見つめる。横顔にも成長がありました。',
        ),
        article(83, '秋へのしたく', '涼しくなった朝のおさんぽ。少し大きな上着を羽織って出発です。'),
      ],
      editorsNote: '何度でも、この一瞬に恋をする。',
    },
  },
  'magazine-cover-weekend': {
    issueLabel: '2026年 秋号',
    publishedOn: '2026年9月7日',
    content: {
      title: 'きみと過ごす、なんでもない日。',
      lead: '散歩も、おやつも、お昼寝も。',
      articles: [
        article(80, '寝顔のとなりで', 'たくさん遊んだあとの静かな寝息。家族も隣でひと休み。'),
        article(
          81,
          'いつもの散歩道',
          '木の葉や小石に立ち止まりながら、いつもの道をゆっくり歩きました。',
        ),
        article(
          82,
          'おやつの時間',
          '焼きたての香りににっこり。小さな手で分けてくれた、午後のおやつ。',
        ),
        article(83, '小さな発見', '雲のかたち、風の音。きみが気づいた世界を、一緒に見つめます。'),
      ],
      editorsNote: '予定を決めない週末に、いちばんの笑顔がありました。',
    },
  },
  'magazine-cover-romantic': {
    issueLabel: '2026年 秋号',
    publishedOn: '2026年9月7日',
    content: {
      title: 'やっぱり、きみがいちばん好き！',
      lead: '今日の主役は、もちろんきみ。',
      articles: [
        article(
          80,
          '今月のとっておき',
          'お気に入りの服を着た日。家族みんなが笑顔になった一枚を選びました。',
        ),
        article(
          81,
          '大好きな横顔',
          '夢中で遊ぶまなざしも、眠る前の顔も。どの表情も残しておきたい。',
        ),
        article(
          82,
          'しあわせリスト',
          'おさんぽ、おやつ、おひるね。家族の「好き」がまたひとつ増えました。',
        ),
        article(
          83,
          'ぎゅっとする時間',
          '帰ってきたら、まずは抱っこ。何度でもうれしい、わが家の習慣。',
        ),
      ],
      editorsNote: 'なんでもない日が、家族の記念日。',
    },
  },
  'magazine-cover-journal': {
    issueLabel: '2026年 秋号',
    publishedOn: '2026年9月7日',
    content: {
      title: 'その瞳に映る世界。',
      lead: '家族の時間を、一枚ずつ。',
      articles: [
        article(
          80,
          'ある日の午後',
          '特別な予定のない日。窓から届く光が、いつもの部屋を静かに照らしていました。',
        ),
        article(
          81,
          'ひかりの記憶',
          '小さな指先、やわらかな頬。あの日の空気まで思い出せる一枚を、この本に。',
        ),
      ],
      editorsNote: 'いつか読み返す日のために、今日のきみを残しておこう。',
    },
  },
  'storybook-page': {
    issueLabel: 'ある夏の日のおはなし',
    publishedOn: '2026年8月5日',
    content: {
      title: 'ひかりをつかまえた日',
      lead: '窓からこぼれる光を、小さな手でそっと追いかけました。',
      articles: [
        article(
          21,
          '朝の光と、小さな手',
          'カーテンが揺れるたび、床の上を光が走ります。追いかけて、手を伸ばして、つかまえたつもりでにっこり。静かな朝が、家族だけの物語になりました。',
        ),
        article(
          22,
          'つづきは明日',
          '明日はどんな光に会えるかな。眠る前に、またひとつ楽しみが増えました。',
        ),
        article(23, '家族のことば', 'いつまでも覚えていたい、やわらかな夏の一日でした。'),
      ],
      editorsNote: 'あなたが見つけた光を、わたしたちも一緒に見つめていました。',
    },
  },
  'growth-report': {
    issueLabel: '1歳6か月',
    publishedOn: '2026年8月5日',
    content: {
      title: '今月の「できた！」',
      lead: '身長82.4cm、体重11.2kg。数字だけでは表せない成長も、ひとつずつ記録しました。',
      articles: [
        article(31, '一人で立つ', '手を離して、三秒間まっすぐ立てました。'),
        article(32, '十二歩あるく', '廊下の端から端まで、自分の足で歩きました。'),
        article(33, 'スプーンに挑戦', 'こぼしながらも、最後のひと口まで自分で食べました。'),
        article(34, '新しいことば', '「おはよう」と「どうぞ」が言えるようになりました。'),
        article(35, '靴を選ぶ', '玄関でお気に入りの靴を指さして教えてくれます。'),
        article(36, '積み木は六個', '慎重に重ねて、崩れると声を上げて笑います。'),
        article(37, '歯みがき上手', '鏡を見ながら、自分で歯ブラシを動かせました。'),
        article(38, 'ぎゅっとお手伝い', '洗濯物を一枚ずつ渡してくれるようになりました。'),
      ],
      editorsNote: '比べるのは昨日の自分。来月の成長も楽しみです。',
    },
  },
};

const imagePalettes = [
  ['#f4d6b8', '#c96b4b', '#6b877a'],
  ['#c9d9df', '#58758c', '#e9bc78'],
  ['#ead7df', '#a85c78', '#758b6f'],
  ['#d8dfc7', '#637b52', '#d99765'],
] as const;

function inlineMockImage(index: number): RenderImage {
  const palette = imagePalettes[index]!;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 1600">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop stop-color="${palette[0]}"/><stop offset="1" stop-color="${palette[1]}"/></linearGradient></defs>
    <rect width="1200" height="1600" fill="url(#g)"/>
    <circle cx="${250 + index * 180}" cy="${330 + index * 110}" r="250" fill="${palette[2]}" opacity=".62"/>
    <path d="M0 1220 Q300 ${930 - index * 40} 600 1190 T1200 1060 V1600 H0Z" fill="#fff" opacity=".48"/>
    <path d="M0 1390 Q320 1160 690 1360 T1200 1230 V1600 H0Z" fill="${palette[2]}" opacity=".72"/>
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    alt: `レイアウト確認用の写真 ${index + 1}`,
    focalX: 45 + index * 4,
    focalY: 42 + index * 3,
  };
}

/** Original sample photography, framed to exercise the reference cover composition. */
function portraitMockImage(): RenderImage {
  return {
    url: '/mock-assets/portrait-magazine-sample.jpg',
    alt: 'レイアウト確認用の笑顔の子どものポートレート',
    focalX: 52,
    focalY: 46,
  };
}

function cloneContent(content: GeneratedIssueContent): GeneratedIssueContent {
  return {
    ...content,
    articles: content.articles.map((item) => ({
      ...item,
      sourceEventIds: [...item.sourceEventIds],
    })),
  };
}

/**
 * Serializable index for a browser gallery. Every item maps to one supported
 * template/output composition; no mobile runtime or Cloudflare binding is required.
 */
export const layoutMockCatalog: readonly LayoutMockCatalogItem[] = Object.values(
  templateManifests,
).flatMap((manifest) =>
  manifest.supportedFormats.map((format) => {
    const profile = renderProfiles[format];
    return {
      id: `${manifest.id}--${format}` as LayoutMockId,
      template: manifest.id,
      templateVersion: manifest.version,
      templateName: manifest.displayName,
      templateDescription: manifest.description,
      format,
      formatName: formatNames[format],
      profile: {
        mimeType: profile.mimeType,
        width: profile.width,
        height: profile.height,
        unit: profile.unit,
        dpi: profile.dpi,
        safeMargin: profile.safeMargin,
      },
    } satisfies LayoutMockCatalogItem;
  }),
);

export function findLayoutMock(id: string): LayoutMockCatalogItem | undefined {
  return layoutMockCatalog.find((item) => item.id === id);
}

/** Returns a fresh fixture so browser-side controls can safely modify their copy. */
export function createLayoutMockInput(id: string): RenderIssueInput {
  const catalogItem = findLayoutMock(id);
  if (!catalogItem) throw new Error(`Unknown layout mock: ${id}`);

  assertSupportedFormat(catalogItem.template, catalogItem.format);
  const fixture = mockFixtures[catalogItem.template];
  // Each gallery fixture mirrors the visible photo furniture of its fixed cover design.
  // The colorful cover has a hero and best shot; the portrait cover is intentionally one-photo.
  const fixturePalettes =
    catalogItem.template === 'magazine-cover' ? imagePalettes.slice(0, 2) : imagePalettes;
  const images = [
    'magazine-cover-portrait',
    'magazine-cover-mode',
    'magazine-cover-weekend',
    'magazine-cover-romantic',
    'magazine-cover-journal',
  ].includes(catalogItem.template)
    ? [portraitMockImage()]
    : fixturePalettes.map((_, index) => inlineMockImage(index));
  // A real portrait makes photo/type balance reviewable across the entire collection.
  // Keep the secondary color plates distinct so multi-photo placement remains easy to audit.
  if (images[0]) images[0] = portraitMockImage();
  if (catalogItem.template === 'magazine-cover' && images[0]) {
    images[0] = { ...images[0], focalY: 68 };
  }

  return {
    template: catalogItem.template,
    templateVersion: catalogItem.templateVersion,
    format: catalogItem.format,
    content: cloneContent(fixture.content),
    issueLabel: fixture.issueLabel,
    publishedOn: fixture.publishedOn,
    images,
    showBrand: true,
    locale: 'ja-JP',
  };
}

/** Produces a complete document suitable for an iframe `srcDoc`. */
export function renderLayoutMock(id: string): string {
  return renderIssueDocument(createLayoutMockInput(id));
}
